<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

const props = defineProps({
  coordinates: { type: Array, default: () => [] },
  zoneName: { type: String, default: '' },
});

const emit = defineEmits(['update:coordinates', 'update:zoneName', 'cepFound']);

const mapContainer = ref(null);
const searchQuery = ref('');
const searchResults = ref([]);
const showResults = ref(false);
const mapInstance = ref(null);
const drawnItems = ref(null);
const currentPolygon = ref(null);
const boundaryLayer = ref(null);
const isLoadingCep = ref(false);
const isSearching = ref(false);
const isLocating = ref(false);
const mapReady = ref(false);
let searchTimeout = null;

const GOIANIA_CENTER = [-16.6869, -49.2648];

// ---- Leaflet Draw PT-BR ----

const setDrawLocale = () => {
  const d = L.drawLocal;
  d.draw.toolbar.actions = { title: 'Cancelar desenho', text: 'Cancelar' };
  d.draw.toolbar.finish = { title: 'Finalizar desenho', text: 'Finalizar' };
  d.draw.toolbar.undo = {
    title: 'Remover ultimo ponto',
    text: 'Remover ultimo ponto',
  };
  d.draw.toolbar.buttons = { polygon: 'Desenhar zona de entrega' };
  d.draw.handlers.polygon = {
    tooltip: {
      start: 'Clique para iniciar a zona.',
      cont: 'Clique para continuar desenhando.',
      end: 'Clique no primeiro ponto para fechar a zona.',
    },
  };
  d.edit.toolbar.actions = {
    save: { title: 'Salvar alteracoes', text: 'Salvar' },
    cancel: { title: 'Cancelar edicao', text: 'Cancelar' },
    clearAll: { title: 'Remover tudo', text: 'Limpar' },
  };
  d.edit.toolbar.buttons = {
    edit: 'Editar zona',
    editDisabled: 'Nenhuma zona para editar',
    remove: 'Remover zona',
    removeDisabled: 'Nenhuma zona para remover',
  };
  d.edit.handlers = {
    edit: {
      tooltip: {
        text: 'Arraste os pontos para editar a zona.',
        subtext: 'Clique em Cancelar para desfazer.',
      },
    },
    remove: { tooltip: { text: 'Clique na zona para remover.' } },
  };
};

// ---- Utility functions ----

const getCentroidLatLng = coords => {
  const sum = coords.reduce(
    (acc, c) => [acc[0] + c.lat, acc[1] + c.lng],
    [0, 0]
  );
  return [sum[0] / coords.length, sum[1] / coords.length];
};

const formatCep = cep => {
  const digits = cep.replace(/\D/g, '');
  if (digits.length > 5)
    return digits.substring(0, 5) + '-' + digits.substring(5);
  return digits;
};

// ---- CEP / Geocoding ----

const expandCepRange = async ceps => {
  const cepStart = ceps[0];
  const cepEnd = ceps[ceps.length - 1];
  emit('cepFound', { cep_start: cepStart, cep_end: cepEnd });
};

const reverseGeocodeBounds = async coords => {
  if (coords.length === 0) return;

  isLoadingCep.value = true;

  const centroid = getCentroidLatLng(coords);
  const samplePoints = [
    centroid,
    ...coords.slice(0, 6).map(c => [c.lat, c.lng]),
  ];
  const cepSet = new Set();
  let detectedName = '';

  const promises = samplePoints.map(async point => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${point[0]}&lon=${point[1]}&addressdetails=1&accept-language=pt-BR`,
        { headers: { 'User-Agent': 'NT Sales-DeliveryZones/1.0' } }
      );
      const data = await res.json();

      if (data?.address) {
        if (data.address.postcode) {
          const cep = data.address.postcode.replace(/\D/g, '');
          if (cep.length === 8) cepSet.add(cep);
        }
        if (!detectedName) {
          detectedName =
            data.address.suburb ||
            data.address.neighbourhood ||
            data.address.city_district ||
            '';
        }
      }
    } catch {
      // Nominatim can fail silently
    }
  });

  await Promise.all(promises);

  if (cepSet.size > 0) {
    const ceps = [...cepSet].sort();
    await expandCepRange(ceps);
  }

  if (detectedName && !props.zoneName) {
    emit('update:zoneName', detectedName);
  }

  isLoadingCep.value = false;
};

// ---- Polygon handling ----

const handlePolygonChange = () => {
  if (!currentPolygon.value) return;

  const latLngs = currentPolygon.value.getLatLngs()[0];
  const coords = latLngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));

  emit('update:coordinates', coords);
  reverseGeocodeBounds(coords);
};

const restorePolygon = coords => {
  if (!mapInstance.value || coords.length === 0) return;

  if (currentPolygon.value) {
    drawnItems.value.removeLayer(currentPolygon.value);
  }

  const latLngs = coords.map(c => [c.lat, c.lng]);
  currentPolygon.value = L.polygon(latLngs, {
    color: '#2563eb',
    fillColor: '#3b82f6',
    fillOpacity: 0.2,
    weight: 2,
  });
  drawnItems.value.addLayer(currentPolygon.value);
  mapInstance.value.fitBounds(currentPolygon.value.getBounds(), {
    padding: [40, 40],
  });
};

const clearPolygon = () => {
  if (currentPolygon.value && drawnItems.value) {
    drawnItems.value.removeLayer(currentPolygon.value);
    currentPolygon.value = null;
  }
  emit('update:coordinates', []);
};

const clearBoundary = () => {
  if (boundaryLayer.value && mapInstance.value) {
    mapInstance.value.removeLayer(boundaryLayer.value);
    boundaryLayer.value = null;
  }
};

// ---- OSM Boundary fetch ----

const fetchNeighborhoodBoundary = async (name, lat, lng) => {
  clearBoundary();
  try {
    // Search for the neighborhood boundary polygon from Nominatim
    const encodedQuery = encodeURIComponent(name);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&polygon_geojson=1&limit=1&accept-language=pt-BR`,
      { headers: { 'User-Agent': 'NT Sales-DeliveryZones/1.0' } }
    );
    const data = await res.json();

    if (data.length > 0 && data[0].geojson) {
      const geojson = data[0].geojson;
      if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
        boundaryLayer.value = L.geoJSON(geojson, {
          style: {
            color: '#f59e0b',
            fillColor: '#fbbf24',
            fillOpacity: 0.1,
            weight: 2,
            dashArray: '6 4',
          },
        }).addTo(mapInstance.value);

        mapInstance.value.fitBounds(boundaryLayer.value.getBounds(), {
          padding: [40, 40],
        });

        return geojson;
      }
    }

    // Fallback: just center on the point
    if (lat && lng) {
      mapInstance.value.setView([lat, lng], 15);
    }
  } catch {
    if (lat && lng) {
      mapInstance.value.setView([lat, lng], 15);
    }
  }
  return null;
};

const useBoundaryAsZone = () => {
  if (!boundaryLayer.value) return;

  const geojson = boundaryLayer.value.toGeoJSON();
  let coords = [];

  // Extract coordinates from GeoJSON
  const features = geojson.features || [geojson];
  const geometry = features[0]?.geometry;

  if (geometry?.type === 'Polygon') {
    coords = geometry.coordinates[0].map(c => ({
      lat: c[1],
      lng: c[0],
    }));
  } else if (geometry?.type === 'MultiPolygon') {
    // Use the largest polygon
    let maxLen = 0;
    geometry.coordinates.forEach(poly => {
      if (poly[0].length > maxLen) {
        maxLen = poly[0].length;
        coords = poly[0].map(c => ({ lat: c[1], lng: c[0] }));
      }
    });
  }

  if (coords.length > 0) {
    // Remove the last point (duplicate of first in GeoJSON)
    if (
      coords.length > 1 &&
      coords[0].lat === coords[coords.length - 1].lat &&
      coords[0].lng === coords[coords.length - 1].lng
    ) {
      coords.pop();
    }

    // Simplify if too many points (>100)
    if (coords.length > 100) {
      const step = Math.ceil(coords.length / 80);
      coords = coords.filter((_, i) => i % step === 0);
    }

    // Set as the drawn polygon
    if (currentPolygon.value) {
      drawnItems.value.removeLayer(currentPolygon.value);
    }

    const latLngs = coords.map(c => [c.lat, c.lng]);
    currentPolygon.value = L.polygon(latLngs, {
      color: '#2563eb',
      fillColor: '#3b82f6',
      fillOpacity: 0.2,
      weight: 2,
    });
    drawnItems.value.addLayer(currentPolygon.value);

    clearBoundary();

    emit('update:coordinates', coords);
    reverseGeocodeBounds(coords);
  }
};

// ---- Geolocation ----

const useMyLocation = () => {
  if (!navigator.geolocation) return;

  isLocating.value = true;
  navigator.geolocation.getCurrentPosition(
    position => {
      const { latitude, longitude } = position.coords;
      if (mapInstance.value) {
        mapInstance.value.setView([latitude, longitude], 16);
        L.marker([latitude, longitude], {
          icon: L.divIcon({
            className: '',
            html: '<div style="width:14px;height:14px;background:#2563eb;border:3px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>',
            iconSize: [14, 14],
            iconAnchor: [7, 7],
          }),
        }).addTo(mapInstance.value);
      }
      isLocating.value = false;
    },
    () => {
      isLocating.value = false;
    },
    { enableHighAccuracy: true, timeout: 8000 }
  );
};

// ---- Search ----

const searchByCep = async cep => {
  isSearching.value = true;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await res.json();
    if (!data.erro) {
      searchResults.value = [
        {
          display: `${data.logradouro ? data.logradouro + ', ' : ''}${data.bairro}, ${data.localidade} - ${data.uf} (${formatCep(cep)})`,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf,
          cep,
          query: `${data.bairro}, ${data.localidade}, ${data.uf}, Brasil`,
        },
      ];
      showResults.value = true;
    }
  } catch {
    // ignore
  } finally {
    isSearching.value = false;
  }
};

const searchByAddress = async query => {
  isSearching.value = true;
  try {
    const encodedQuery = encodeURIComponent(query + ', Brasil');
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=5&addressdetails=1&accept-language=pt-BR`,
      { headers: { 'User-Agent': 'NT Sales-DeliveryZones/1.0' } }
    );
    const data = await res.json();
    searchResults.value = data.map(item => ({
      display: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      neighborhood:
        item.address?.suburb ||
        item.address?.neighbourhood ||
        item.address?.city_district ||
        '',
      boundingbox: item.boundingbox,
      osmType: item.osm_type,
      osmId: item.osm_id,
      type: item.type,
      category: item.class,
    }));
    showResults.value = searchResults.value.length > 0;
  } catch {
    // ignore
  } finally {
    isSearching.value = false;
  }
};

const handleSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout);

  const query = searchQuery.value.trim();
  if (query.length < 3) {
    searchResults.value = [];
    showResults.value = false;
    return;
  }

  const cepOnly = query.replace(/\D/g, '');
  if (cepOnly.length === 8) {
    searchTimeout = setTimeout(() => searchByCep(cepOnly), 300);
    return;
  }

  searchTimeout = setTimeout(() => searchByAddress(query), 400);
};

const selectResult = async result => {
  showResults.value = false;
  searchQuery.value = '';

  // If from ViaCEP, need to geocode the address first
  if (result.query && !result.lat) {
    try {
      const encodedQuery = encodeURIComponent(result.query);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=1&accept-language=pt-BR`,
        { headers: { 'User-Agent': 'NT Sales-DeliveryZones/1.0' } }
      );
      const data = await res.json();
      if (data.length > 0) {
        result.lat = parseFloat(data[0].lat);
        result.lng = parseFloat(data[0].lon);
        result.boundingbox = data[0].boundingbox;
      }
    } catch {
      return;
    }
  }

  if (result.neighborhood && !props.zoneName) {
    emit('update:zoneName', result.neighborhood);
  }

  if (result.cep) {
    emit('cepFound', { cep_start: result.cep, cep_end: result.cep });
  }

  // Try to fetch boundary for the searched place
  if (result.neighborhood && result.lat && result.lng) {
    const searchName =
      result.neighborhood +
      (result.city ? ', ' + result.city : '') +
      ', Brasil';
    await fetchNeighborhoodBoundary(searchName, result.lat, result.lng);
  } else if (result.lat && result.lng && mapInstance.value) {
    if (result.boundingbox) {
      const bounds = [
        [parseFloat(result.boundingbox[0]), parseFloat(result.boundingbox[2])],
        [parseFloat(result.boundingbox[1]), parseFloat(result.boundingbox[3])],
      ];
      mapInstance.value.fitBounds(bounds, { padding: [40, 40] });
    } else {
      mapInstance.value.setView([result.lat, result.lng], 15);
    }

    // If it's a neighborhood-type result, try fetching boundary
    const placeTypes = ['suburb', 'neighbourhood', 'quarter', 'residential'];
    if (placeTypes.includes(result.type)) {
      await fetchNeighborhoodBoundary(result.display, result.lat, result.lng);
    }
  }
};

const dismissResults = () => {
  setTimeout(() => {
    showResults.value = false;
  }, 200);
};

// ---- Map init ----

const initMap = () => {
  if (!mapContainer.value) return;

  setDrawLocale();

  const center =
    props.coordinates.length > 0
      ? getCentroidLatLng(props.coordinates)
      : GOIANIA_CENTER;

  mapInstance.value = L.map(mapContainer.value, {
    center,
    zoom: 14,
    zoomControl: false,
  });

  // Custom zoom position
  L.control.zoom({ position: 'bottomright' }).addTo(mapInstance.value);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap',
    maxZoom: 19,
  }).addTo(mapInstance.value);

  drawnItems.value = new L.FeatureGroup();
  mapInstance.value.addLayer(drawnItems.value);

  const drawControl = new L.Control.Draw({
    position: 'topright',
    draw: {
      polygon: {
        allowIntersection: false,
        showArea: true,
        shapeOptions: {
          color: '#2563eb',
          fillColor: '#3b82f6',
          fillOpacity: 0.2,
          weight: 2,
        },
      },
      polyline: false,
      rectangle: false,
      circle: false,
      marker: false,
      circlemarker: false,
    },
    edit: {
      featureGroup: drawnItems.value,
      remove: true,
    },
  });
  mapInstance.value.addControl(drawControl);

  mapInstance.value.on(L.Draw.Event.CREATED, e => {
    if (currentPolygon.value) {
      drawnItems.value.removeLayer(currentPolygon.value);
    }
    clearBoundary();
    currentPolygon.value = e.layer;
    drawnItems.value.addLayer(currentPolygon.value);
    handlePolygonChange();
  });

  mapInstance.value.on(L.Draw.Event.EDITED, () => {
    handlePolygonChange();
  });

  mapInstance.value.on(L.Draw.Event.DELETED, () => {
    currentPolygon.value = null;
    emit('update:coordinates', []);
  });

  if (props.coordinates.length > 0) {
    restorePolygon(props.coordinates);
  }

  mapReady.value = true;
};

watch(
  () => props.coordinates,
  newCoords => {
    if (mapInstance.value && newCoords.length > 0 && !currentPolygon.value) {
      restorePolygon(newCoords);
    }
  }
);

onMounted(async () => {
  await nextTick();
  initMap();
});

onBeforeUnmount(() => {
  if (mapInstance.value) {
    mapInstance.value.remove();
    mapInstance.value = null;
  }
});
</script>

<template>
  <div class="space-y-2">
    <!-- Search + locate row -->
    <div class="flex gap-2">
      <div class="relative flex-1">
        <span
          class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-11"
        />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Buscar bairro, endereco ou CEP..."
          class="w-full pl-9 pr-3 py-2 text-sm border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10"
          @input="handleSearch"
          @blur="dismissResults"
        />
        <span
          v-if="isSearching"
          class="i-lucide-loader-2 absolute right-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-11 animate-spin"
        />

        <!-- Search results dropdown -->
        <div
          v-if="showResults && searchResults.length > 0"
          class="absolute z-[1000] mt-1 w-full bg-white dark:bg-n-solid-3 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          <button
            v-for="(result, index) in searchResults"
            :key="index"
            type="button"
            class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors border-b border-n-weak last:border-b-0"
            @mousedown.prevent="selectResult(result)"
          >
            <span class="line-clamp-2">{{ result.display }}</span>
          </button>
        </div>
      </div>

      <button
        type="button"
        class="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-n-weak rounded-lg bg-n-alpha-1 text-n-slate-12 hover:border-woot-500 hover:text-woot-500 transition-colors whitespace-nowrap"
        :disabled="isLocating"
        @click="useMyLocation"
      >
        <span
          :class="
            isLocating ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-crosshair'
          "
          class="size-4"
        />
        Minha localizacao
      </button>
    </div>

    <!-- Map -->
    <div
      ref="mapContainer"
      class="w-full h-96 rounded-lg border border-n-weak overflow-hidden bg-n-alpha-2 z-0"
    />

    <!-- Boundary suggestion banner -->
    <div
      v-if="boundaryLayer"
      class="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2"
    >
      <p class="text-xs text-amber-800 dark:text-amber-300">
        Contorno do bairro encontrado (tracejado amarelo). Deseja usar como
        zona?
      </p>
      <div class="flex gap-2">
        <button
          type="button"
          class="text-xs font-medium text-amber-800 dark:text-amber-300 hover:underline"
          @click="clearBoundary"
        >
          Ignorar
        </button>
        <button
          type="button"
          class="text-xs font-medium px-2.5 py-1 rounded-md bg-woot-500 text-white hover:bg-woot-600 transition-colors"
          @click="useBoundaryAsZone"
        >
          Usar contorno
        </button>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-between">
      <p class="text-xs text-n-slate-11">
        <span v-if="isLoadingCep" class="flex items-center gap-1">
          <span class="i-lucide-loader-2 size-3 animate-spin" />
          Buscando CEPs da regiao...
        </span>
        <span v-else-if="!currentPolygon && !boundaryLayer">
          Busque um bairro e use o contorno, ou desenhe manualmente.
        </span>
        <span
          v-else-if="currentPolygon"
          class="flex items-center gap-1 text-green-600 dark:text-green-400"
        >
          <span class="i-lucide-check-circle size-3" />
          Zona delimitada no mapa.
        </span>
      </p>
      <button
        v-if="currentPolygon"
        type="button"
        class="text-xs text-red-500 hover:text-red-600 font-medium"
        @click="clearPolygon"
      >
        Limpar desenho
      </button>
    </div>
  </div>
</template>
